import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import DeskDashboard from "../../components/desk/DeskDashboard";
import InquiryComposer from "../../components/desk/InquiryComposer";
import Rollup from "../../components/desk/Rollup";
import { useAuth } from "../../js/hooks/useAuth";
import { useBrokerDesk } from "../../js/hooks/useBrokerDesk";
import { useJourneyLive } from "../../js/hooks/useJourneyLive";
import { getBrokerOfficeSnapshot } from "../../js/api/messaging/office";
import { BROKER_DESK } from "../../js/api/brokerDesk";
import { buildThreadItems } from "../../js/api/messaging/helpers";
import { payUrl, seatLabel, seatMax, JOURNEY_LINK_PROPS } from "../../js/utils/siteLinks";
import { prettyValue } from "../../js/utils/formatters";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { parseFilmSearch } from "@tour/film.js";
import DeskPlatformBadge from "../../components/desk/DeskPlatformBadge";

function unreadFor(rows, userId) {
  return buildThreadItems(rows, userId).reduce((sum, item) => sum + (item.unread || 0), 0);
}

export default function BrokerDashboard() {
  const { session } = useAuth();
  const { search } = useLocation();
  const { mode, setMode } = useBrokerDesk();
  const [pulse, setPulse] = useState(0);
  const filmOn = parseFilmSearch(search).on;

  useEffect(() => {
    if (filmOn && mode === BROKER_DESK.mine) setMode(BROKER_DESK.office);
  }, [filmOn, mode, setMode]);
  const snapshot = useMemo(() => getBrokerOfficeSnapshot(session), [session, pulse]);
  const maxSeats = seatMax(snapshot.seat_tier);
  const overCapacity = snapshot.agents.length > maxSeats;
  const mine = mode === BROKER_DESK.mine;
  const mineUnread = unreadFor(snapshot.mine, session?.userId);
  const officeUnread = unreadFor(snapshot.office, session?.userId);

  useJourneyLive(() => setPulse((n) => n + 1));

  return (
    <AppLayout variant="broker">
      <div className="page-header broker-hero" data-tour="broker-hero">
        <div>
          <DeskPlatformBadge />
          <h1>Broker Desk</h1>
          <p className="page-lead">
            Supervise the office without mixing in your own book. My Business and All Agents stay
            apart so the work in front of you stays quiet and clear.
          </p>
        </div>
      </div>

      <div className="broker-split">
        <section className={`broker-pane${mine ? " is-active" : ""}`} data-tour="broker-mine">
          <p className="broker-pane__kicker">Your book</p>
          <h2>My Business</h2>
          <p className="muted">
            Files where you are the agent of record. They never appear under All Agents.
          </p>
          <div className="broker-pane__stats">
            <div>
              <p className="stat-label">Open files</p>
              <p className="stat-value">{snapshot.mineOpen}</p>
            </div>
            <div>
              <p className="stat-label">Unread</p>
              <p className="stat-value">{mineUnread}</p>
            </div>
          </div>
          {snapshot.mine.length ? (
            <ul className="stack broker-pane__list">
              {snapshot.mine.slice(0, 4).map((item) => (
                <li key={item.id}>
                  <strong>{item.property_address || item.id}</strong>
                  <div className="muted">
                    {prettyValue(item.status)} · {prettyValue(item.client_role)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">
              When you take a buyer or listing yourself, it lives here — never in the office rollup.
            </p>
          )}
          {!mine ? (
            <p>
              <button type="button" className="primary-btn" onClick={() => setMode(BROKER_DESK.mine)}>
                Open My Business
              </button>
            </p>
          ) : (
            <p className="row" style={{ gap: "1rem" }}>
              <Link to="/broker/clients" className="text-btn">
                Client Manager →
              </Link>
              <Link to="/broker/messages" className="text-btn">
                Client Messaging →
              </Link>
            </p>
          )}
        </section>

        <section className={`broker-pane${mine ? "" : " is-active"}`} data-tour="broker-office">
          <p className="broker-pane__kicker">Office supervision</p>
          <h2>All Agents</h2>
          <p className="muted">
            Everyone else’s files. Your personal book stays off this list so office activity does not
            bury your own day.
          </p>
          <div className="broker-pane__stats">
            <div>
              <p className="stat-label">Agents</p>
              <p className="stat-value">{snapshot.agents.length}</p>
            </div>
            <div>
              <p className="stat-label">Open files</p>
              <p className="stat-value">{snapshot.officeOpen}</p>
            </div>
            <div>
              <p className="stat-label">Unread</p>
              <p className="stat-value">{officeUnread}</p>
            </div>
          </div>
          {mine ? (
            <p>
              <button type="button" className="primary-btn" onClick={() => setMode(BROKER_DESK.office)}>
                Open All Agents
              </button>
            </p>
          ) : (
            <p className="row" style={{ gap: "1rem" }}>
              <Link to="/broker/messages" className="text-btn">
                Client Messaging →
              </Link>
              <Link to="/broker/office" className="text-btn">
                Office Messaging →
              </Link>
              <Link to="/broker/audit" className="text-btn">
                Retention log →
              </Link>
            </p>
          )}
        </section>
      </div>

      {mine ? (
        <div className="desk-panel broker-work">
          <DeskDashboard
            heading="My Working Desk"
            lead="Calendar, clients, and inquiries for your personal book only. The office stays on All Agents."
          />
        </div>
      ) : (
        <>
          {overCapacity ? (
            <p className="confirm-banner">
              This office is over the {seatLabel(snapshot.seat_tier)} plan. You can keep working — seat
              limits will apply when billing is connected.
            </p>
          ) : null}

          <div className="two-col">
            <div className="card">
              <h2>Agents</h2>
              {snapshot.agents.length ? (
                <ul className="stack" style={{ listStyle: "none", padding: 0 }}>
                  {snapshot.agents.map((agent) => (
                    <li key={agent.id}>
                      <strong>{agent.name}</strong>
                      <div className="muted">
                        {agent.email}
                        {agent.office_name ? ` · ${agent.office_name}` : ""}
                        {agent.plan_status ? ` · ${prettyValue(agent.plan_status)}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No agents are linked to this office yet.</p>
              )}
            </div>

            <div className="card">
              <h2>Office Files</h2>
              <p className="muted">
                {snapshot.officeOpen} open · {snapshot.office.length - snapshot.officeOpen} archived
                among agent files
              </p>
              <ul className="stack" style={{ listStyle: "none", padding: 0 }}>
                {snapshot.office.slice(0, 6).map((item) => (
                  <li key={item.id}>
                    <strong>{item.property_address || item.id}</strong>
                    <div className="muted">
                      {prettyValue(item.status)} · {prettyValue(item.client_role)}
                      {item.agent?.name ? ` · ${item.agent.name}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="row" style={{ gap: "1rem", marginTop: "0.75rem" }}>
                <Link to="/broker/messages" className="text-btn">
                  Client Messaging →
                </Link>
                <Link to="/broker/office" className="text-btn">
                  Office Messaging →
                </Link>
                <Link to="/broker/messages/archive" className="text-btn">
                  Archive →
                </Link>
                <Link to="/broker/audit" className="text-btn">
                  Retention log →
                </Link>
              </p>
            </div>

            <div className="card" data-tour="broker-billing">
              <p>
                Current plan: {seatLabel(snapshot.seat_tier)}
                {snapshot.plan_status ? ` · ${prettyValue(snapshot.plan_status)}` : ""}
              </p>
              <p className="muted">
                Payment lives on dreamhomejourney.online through PayPal. This desk stays a workflow, not a storefront.
              </p>
              <p>
                <a className="primary-btn" href={payUrl(session)} {...JOURNEY_LINK_PROPS}>
                  Manage billing
                </a>
              </p>
            </div>

            <div className="card">
              <h2>Notify an Agent</h2>
              <InquiryComposer desk="broker" />
            </div>
          </div>

          <div className="desk-panel broker-work">
            <DeskDashboard
              heading="Office Desk Operations"
              lead="The same working desk as agents — calendar, clients, inquiries, and files — for the office only. Your personal book is on My Business."
            />
          </div>

          <div className="desk-panel">
            <h2>Ready for Broker Interviews</h2>
            <p className="page-lead">
              Placeholder sections — empty on purpose so requested tools can land without reshuffling the
              desk.
            </p>
            <div className="two-col">
              <Rollup title="Recruiting & onboarding">
                <p className="muted placeholder-panel card">
                  Seat invites, new-agent welcome packets, and office policy acknowledgments will live
                  here after interviews.
                </p>
              </Rollup>
              <Rollup title="Compliance reviews">
                <p className="muted placeholder-panel card">
                  File sampling, advertising checks, and supervision notes — retention-aligned, not a DRE
                  certificate.
                </p>
              </Rollup>
              <Rollup title="Office training">
                <p className="muted placeholder-panel card">
                  Checklist walkthroughs and seller-portfolio briefings for the team, when you ask for
                  them.
                </p>
              </Rollup>
              <Rollup title="Custom report packs">
                <p className="muted placeholder-panel card">
                  Broker-branded export templates requested during office interviews.
                </p>
              </Rollup>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
